import { useCallback, useEffect, useRef } from 'react';
import { type NavigateOptions, useSearchParams } from 'react-router-dom';
import { useHub } from '@/contexts/HubContext';
import { useHubNavigate, useHubPathname } from '@/lib/global/hub-path';
import type { Client } from '@/types';
import {
  CLIENT_DASHBOARD_PATH,
  FINANCE_CANONICAL_ROOT,
  FINANCE_CLIENT_PARAM,
  isFinanceLogicalPath,
  splitHref,
  withBookSearch,
  withoutTrailingSlash,
} from './paths';

export type FinanceBook = { kind: 'operator' } | { kind: 'client'; clientId: string } | { kind: 'mine' };

export const MINE_BOOK = 'mine';

/** The server's all-clients sentinel (`client_scope_guard.py` reads `*` as "no specific client"). */
export const WILDCARD_BOOK = '*';

export function parseFinanceBook(raw: string | null): FinanceBook {
  const value = raw?.trim() ?? '';
  if (value === '' || value === WILDCARD_BOOK) return { kind: 'operator' };
  if (value.toLowerCase() === MINE_BOOK) return { kind: 'mine' };
  return { kind: 'client', clientId: value };
}

export function bookQueryValue(book: FinanceBook): string | null {
  switch (book.kind) {
    case 'operator':
      return null;
    case 'client':
      return book.clientId;
    case 'mine':
      return MINE_BOOK;
    default: {
      const unreachable: never = book;
      return unreachable;
    }
  }
}

/** A value-based identity: URL entry and back/forward must not depend on object identity. */
export function financeBookIdentity(book: FinanceBook): string {
  const value = bookQueryValue(book);
  return value === null ? 'operator' : `${book.kind}:${value}`;
}

/** Client id for an API that accepts optional `client_id`. Operator and `mine` omit it. */
export function apiClientId(book: FinanceBook): string | undefined {
  switch (book.kind) {
    case 'client':
      return book.clientId;
    case 'operator':
    case 'mine':
      return undefined;
    default: {
      const unreachable: never = book;
      return unreachable;
    }
  }
}

/**
 * Settings `client` value for this book, or `null` to omit the param.
 * Empty string is explicit operator scope. It is not `bookQueryValue`, which is null
 * for the operator book and would delete the param. `mine` omits the param: a
 * named-grant user has no operator-wide settings scope, and an absent param keeps
 * Hub's current client selection.
 */
export function settingsClientParam(book: FinanceBook): string | null {
  switch (book.kind) {
    case 'client':
      return book.clientId;
    case 'operator':
      return '';
    case 'mine':
      return null;
    default: {
      const unreachable: never = book;
      return unreachable;
    }
  }
}

/** Empty/blank ids and the reserved `mine` / `*` sentinels are not usable client grants. */
export function validFinanceClientIds(clientIds: readonly unknown[]): string[] {
  return clientIds.filter(
    (id): id is string => typeof id === 'string' && id.trim().length > 0 && id !== WILDCARD_BOOK && id.toLowerCase() !== MINE_BOOK
  );
}

export function defaultBook(wildcard: boolean, clientIds: readonly unknown[]): FinanceBook {
  const validIds = validFinanceClientIds(clientIds);
  if (wildcard) return { kind: 'operator' };
  if (validIds.length === 1) return { kind: 'client', clientId: validIds[0] };
  // Zero grants also land on `mine`, which renders a stable no-client-access surface.
  return { kind: 'mine' };
}

/**
 * Book to write on entry. `null` means the URL already says the right thing.
 *
 * An explicit client id in the URL is left untouched even when the grant will reject it:
 * `/users/me/clients` is paginated and can be truncated, so only the server can say
 * whether an id is outside the grant (it answers 403).
 */
export function bookOnEntry(raw: string | null, wildcard: boolean, clientIds: readonly unknown[]): FinanceBook | null {
  const explicit = raw?.trim() ?? '';
  // `*` is the server's "no specific client", never a grant: resolve the default book
  // and let the writer normalize the sentinel out of the URL.
  if (explicit === WILDCARD_BOOK) return defaultBook(wildcard, clientIds);
  if (explicit && explicit.toLowerCase() !== MINE_BOOK) return null;
  const target = defaultBook(wildcard, clientIds);
  return financeBookIdentity(target) === financeBookIdentity(parseFinanceBook(raw)) ? null : target;
}

/** The operator dashboard is the root, the client dashboard is `/overview`. */
function dashboardPathForBook(path: string, book: FinanceBook): string {
  const normalized = withoutTrailingSlash(path);
  if (!isFinanceLogicalPath(normalized)) return normalized;
  const suffix = normalized.slice(FINANCE_CANONICAL_ROOT.length);
  if (suffix === '' && book.kind !== 'operator') return CLIENT_DASHBOARD_PATH;
  if (suffix === '/overview' && book.kind === 'operator') return FINANCE_CANONICAL_ROOT;
  return normalized;
}

/** The current book written onto a finance path: dashboard path for the book, other query params kept. */
export function financeHref(href: string, book: FinanceBook): string {
  const { path, search } = splitHref(href);
  return withBookSearch(dashboardPathForBook(path, book), search, bookQueryValue(book));
}

export function useFinanceBook(): FinanceBook {
  const [params] = useSearchParams();
  return parseFinanceBook(params.get(FINANCE_CLIENT_PARAM));
}

export interface FinanceSession {
  book: FinanceBook;
  /** Raw `client` query. `null` when the param is absent. */
  rawClient: string | null;
  wildcard: boolean;
  /** Selected operator id. A change invalidates every book — see `useFinanceBookSync`. */
  operatorId: string | null;
  ready: boolean;
  loading: boolean;
  failed: boolean;
  clientIds: string[];
  clients: Client[];
  operatorName: string;
}

export function useFinanceSession(): FinanceSession {
  const { clients, hasAllClientsAccess, loadingState, errorState, selectedOperator, clientsSettled } = useHub();
  const [params] = useSearchParams();
  const loading = Boolean(loadingState?.clients);
  const failed = Boolean(errorState?.clients);
  const rawClient = params.get(FINANCE_CLIENT_PARAM);
  return {
    book: parseFinanceBook(rawClient),
    rawClient,
    wildcard: Boolean(hasAllClientsAccess),
    operatorId: selectedOperator?.id ?? null,
    ready: !loading && !failed && Boolean(clientsSettled),
    loading,
    failed,
    clientIds: validFinanceClientIds((clients ?? []).map((client) => client.id)),
    clients: clients ?? [],
    operatorName: selectedOperator?.name || 'Operator',
  };
}

/** Entity-scoped params open one record (an invoice drawer, its email flow); they never carry into another book. */
const ENTITY_SEARCH_PARAMS = ['invoice_id', 'email'] as const;

/** Explicit finance choices write only the URL. They never mirror into legacy Hub context. */
export function useFinanceBookWriter() {
  const navigate = useHubNavigate();
  const pathname = useHubPathname();
  const [params] = useSearchParams();

  return useCallback(
    (next: FinanceBook, opts?: { push?: boolean }) => {
      const search = new URLSearchParams(params);
      if (financeBookIdentity(parseFinanceBook(params.get(FINANCE_CLIENT_PARAM))) !== financeBookIdentity(next)) {
        for (const key of ENTITY_SEARCH_PARAMS) search.delete(key);
      }
      const qs = search.toString();
      // An explicit switcher choice is a navigation the user may want to Back out of;
      // entry normalization and sync rewrites replace instead.
      navigate(financeHref(qs ? `${pathname}?${qs}` : pathname, next), { replace: !opts?.push });
    },
    [navigate, params, pathname]
  );
}

export function useFinanceNavigate() {
  const navigate = useHubNavigate();
  const book = useFinanceBook();
  return useCallback(
    (to: string, opts?: NavigateOptions) => {
      navigate(isFinanceLogicalPath(to) ? financeHref(to, book) : to, opts);
    },
    [book, navigate]
  );
}

/** On entry the URL wins. URL normalization never writes legacy context or storage. */
export function useFinanceBookSync() {
  const session = useFinanceSession();
  const pathname = useHubPathname();
  const write = useFinanceBookWriter();
  const clientKey = session.clientIds.join(',');
  const operatorIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session.ready) return;
    const clientIds = clientKey ? clientKey.split(',') : [];
    // An operator switch invalidates every book: keeping the previous operator's
    // client id in the URL would send every query to the wrong scope (403 or empty).
    if (operatorIdRef.current !== null && operatorIdRef.current !== session.operatorId) {
      operatorIdRef.current = session.operatorId;
      write(defaultBook(session.wildcard, clientIds));
      return;
    }
    operatorIdRef.current = session.operatorId;
    const book = parseFinanceBook(session.rawClient);
    const next = bookOnEntry(session.rawClient, session.wildcard, clientIds);
    if (next && financeBookIdentity(book) !== financeBookIdentity(next)) {
      write(next);
      return;
    }
    // Compare the canonical dashboard path, not the href: `financeHref` re-appends the book
    // query, which the pathname never carries, so comparing against it would never converge.
    if (dashboardPathForBook(pathname, book) !== pathname) write(book);
  }, [clientKey, pathname, session.operatorId, session.rawClient, session.ready, session.wildcard, write]);
}
